import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUser, FiMail, FiCheck } = FiIcons;

const Settings = () => {
  const { user, updatePreferences } = useAuth();
  const [preferences, setPreferences] = useState({ daily: true, weekly: true });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (user?.email_preferences) {
      setPreferences(user.email_preferences);
    }
  }, [user]);

  const handlePreferenceChange = async (key, value) => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const newPreferences = { ...preferences, [key]: value };
      await updatePreferences(newPreferences);
      setPreferences(newPreferences);
      setSuccess('Preferencias actualizadas correctamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Error al actualizar preferencias');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-3xl font-bold text-gray-900 mb-8"> Configuración </h1>

          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md mb-6 flex items-center">
              <SafeIcon icon={FiCheck} className="w-4 h-4 mr-2" /> {success}
            </div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6">
              {error}
            </div>
          )}

          <div className="space-y-6">
            {/* Account Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-3 mb-6">
                <SafeIcon icon={FiUser} className="w-6 h-6 text-blue-800" />
                <h2 className="text-xl font-semibold text-gray-900"> Tu Cuenta </h2>
              </div>
              <div className="space-y-4">
                 <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1"> Correo electrónico </label>
                    <div className="text-gray-900">{user?.email}</div>
                 </div>
                 <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1"> Estado </label>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                      Suscripción Activa
                    </span>
                 </div>
              </div>
            </div>

            {/* Email Preferences */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-3 mb-6">
                <SafeIcon icon={FiMail} className="w-6 h-6 text-blue-800" />
                <h2 className="text-xl font-semibold text-gray-900"> Preferencias de envío </h2>
              </div>
              <div className="space-y-6">
                {/* Daily emails */}
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-base font-medium text-gray-900"> Alertas diarias (08:00 AM) </h3>
                    <p className="text-sm text-gray-600 mt-1"> Recibe el resumen matinal con las novedades del día </p>
                  </div>
                  <div className="flex-shrink-0">
                    <button
                      onClick={() => handlePreferenceChange('daily', !preferences.daily)}
                      disabled={loading}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        preferences.daily ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        preferences.daily ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                </div>

                {/* Weekly emails */}
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-base font-medium text-gray-900"> Resumen semanal </h3>
                    <p className="text-sm text-gray-600 mt-1"> Recopilación de lo más importante de la semana (Domingos) </p>
                  </div>
                  <div className="flex-shrink-0">
                    <button
                      onClick={() => handlePreferenceChange('weekly', !preferences.weekly)}
                      disabled={loading}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        preferences.weekly ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        preferences.weekly ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Settings;